import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Boxes } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { StatusDot } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/input";
import { useInventoryBatches } from "@/features/inventory/api";
import {
  BATCH_STATUSES,
  batchStatusTone,
  fmtQty,
  labelFor,
  qualityTone,
  QUALITY_STATUSES,
} from "@/features/inventory/constants";
import type { InventoryBatchListRow } from "@/features/inventory/types";
import { useMaterials } from "@/features/materials/api";
import { useStockLocations } from "@/features/locations/api";
import { isSupabaseConfigured } from "@/lib/supabase";

const columnHelper = createColumnHelper<InventoryBatchListRow>();

export function InventoryPage() {
  return (
    <ErrorBoundary title="Inventory crashed">
      <InventoryPageInner />
    </ErrorBoundary>
  );
}

function InventoryPageInner() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: "received_at", desc: true }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [materialId, setMaterialId] = useState("all");
  const [locationId, setLocationId] = useState("all");
  const [batchStatus, setBatchStatus] = useState("in_stock");

  const { data: materials = [] } = useMaterials();
  const { data: locations = [] } = useStockLocations();
  const { data = [], isLoading, error, refetch, isFetching } = useInventoryBatches({
    materialId: materialId === "all" ? undefined : materialId,
    locationId: locationId === "all" ? undefined : locationId,
    batchStatus,
  });

  useMasterListShortcuts({ searchInputId: "inventory-search" });

  const openBatch = useCallback(
    (row: InventoryBatchListRow) => {
      void navigate({ to: "/inventory/$batchId", params: { batchId: row.id } });
    },
    [navigate],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("batch_no", {
        header: "Batch",
        cell: (info) => (
          <div>
            <div className="font-mono text-xs font-medium">{info.getValue()}</div>
            {info.row.original.po_number ? (
              <div className="text-2xs text-muted-foreground">{info.row.original.po_number}</div>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor("material_name", {
        header: "Material",
        cell: (info) => (
          <div>
            <div className="text-xs font-medium">{info.getValue() ?? "—"}</div>
            <div className="text-2xs text-muted-foreground">
              {[info.row.original.grade_name, info.row.original.shape_label]
                .filter(Boolean)
                .join(" · ") || "—"}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("location_code", {
        header: "Location",
        cell: (info) => (
          <div className="text-xs">
            <span className="font-mono">{info.getValue() ?? "—"}</span>
            {info.row.original.location_name ? (
              <span className="text-muted-foreground"> · {info.row.original.location_name}</span>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor("weight", {
        header: "Wt",
        cell: (info) => (
          <div className="text-right tabular-nums text-xs">
            <div>{fmtQty(info.getValue())}</div>
            <div className="text-2xs text-muted-foreground">
              qty {fmtQty(info.row.original.quantity)}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("batch_status", {
        header: "Status",
        cell: (info) => (
          <StatusDot tone={batchStatusTone(info.getValue())}>
            {labelFor(BATCH_STATUSES, info.getValue())}
          </StatusDot>
        ),
      }),
      columnHelper.accessor("quality_status", {
        header: "QC",
        cell: (info) => (
          <StatusDot tone={qualityTone(info.getValue())}>
            {labelFor(QUALITY_STATUSES, info.getValue())}
          </StatusDot>
        ),
      }),
      columnHelper.accessor("received_at", {
        header: "Received",
        cell: (info) => (
          <span className="tabular-nums text-xs text-muted-foreground">
            {format(parseISO(info.getValue()), "dd MMM yyyy")}
          </span>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _colId, filter) => {
      const q = String(filter ?? "")
        .trim()
        .toLowerCase();
      if (!q) return true;
      const r = row.original;
      return [r.batch_no, r.material_name, r.grade_name, r.location_code, r.vendor_name, r.po_number]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    },
  });

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Configure Supabase to view inventory batches."
      />
    );
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Inventory"
        icon={<Boxes />}
        countLabel={`${data.length} batches`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
      />

      <MasterBanner>
        Stock batches by material and location. Receive from Purchase → PO → Receive.
      </MasterBanner>

      <MasterFilterBar>
        <MasterSearchInput
          id="inventory-search"
          value={globalFilter}
          onChange={setGlobalFilter}
          placeholder="Batch, material, PO…"
        />
        <NativeSelect
          className="h-8 w-40"
          value={materialId}
          onChange={(e) => setMaterialId(e.target.value)}
        >
          <option value="all">All materials</option>
          {materials
            .filter((m) => m.is_active)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
        </NativeSelect>
        <NativeSelect
          className="h-8 w-44"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          <option value="all">All locations</option>
          {locations
            .filter((l) => l.is_active)
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.code} · {l.name}
              </option>
            ))}
        </NativeSelect>
        <NativeSelect
          className="h-8 w-36"
          value={batchStatus}
          onChange={(e) => setBatchStatus(e.target.value)}
        >
          <option value="all">All statuses</option>
          {BATCH_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
      </MasterFilterBar>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : (
        <DataTable
          table={table}
          columns={columns}
          isLoading={isLoading}
          emptyTitle="No batches"
          emptyDescription="Receive goods against a confirmed PO to create stock batches."
          onRowClick={(row) => openBatch(row.original)}
        />
      )}
    </MasterPage>
  );
}
